import React, { useState, useRef, useCallback } from "react";
import useOutsideClickListener from "../../hooks/useOutsideClickListener";
import "./CustomPicky.css";
import { DropdownButton } from "react-bootstrap";

const CustomPicky = ({
  options = [],
  value,
  onChange,
  placeholder = "Select...",
  disabled = false,
  multiple = false,
  className = "",
  numberDisplayed = 1,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const inputRef = useRef(null);

  const handleCloseDropdown = useCallback(() => {
    setIsOpen(false);
    setFilter("");
  }, []);

  const dropdownRef = useOutsideClickListener(handleCloseDropdown);

  const filteredOptions =
    filter === ""
      ? options
      : options.filter((option) =>
          option.label.toLowerCase().includes(filter.toLowerCase())
        );

  const handleToggle = () => {
    if (!disabled) {
      const newIsOpen = !isOpen;
      setIsOpen(newIsOpen);
      if (newIsOpen) {
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 0);
      } else {
        setFilter("");
      }
    }
  };

  const handleOptionClick = (option) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const isSelected = currentValues.some(
        (v) => v && v.value === option.value
      );

      if (isSelected) {
        const newValues = currentValues.filter(
          (v) => v && v.value !== option.value
        );
        onChange(newValues);
      } else {
        onChange([...currentValues, option]);
      }
    } else {
      onChange(option);
      setIsOpen(false);
      setFilter("");
    }
  };

  const handleSelectAll = () => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const allSelected = filteredOptions.every((option) =>
        currentValues.some((v) => v && v.value === option.value)
      );

      if (allSelected) {
        // Deselect all filtered options
        const newValues = currentValues.filter(
          (v) => !filteredOptions.some((option) => option.value === v.value)
        );
        onChange(newValues);
      } else {
        // Select all filtered options
        const newValues = [...currentValues];
        filteredOptions.forEach((option) => {
          if (!newValues.some((v) => v && v.value === option.value)) {
            newValues.push(option);
          }
        });
        onChange(newValues);
      }
    }
  };

  const isSelected = (option) => {
    if (multiple) {
      return (
        Array.isArray(value) && value.some((v) => v && v.value === option.value)
      );
    }

    if (!value) {
      return false;
    }

    if (Array.isArray(value)) {
      return value.some((v) => v && v.value === option.value);
    }

    return value.value === option.value;
  };

  const getDisplayValue = () => {
    if (multiple) {
      if (!value || !Array.isArray(value) || value.length === 0) {
        return placeholder;
      }
      if (value.length === 1) {
        return value[0].label;
      }
      if (value.length === options.length) {
        return "All selected";
      }
      return `${value.length} selected`;
    }

    if (!value) {
      return placeholder;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return placeholder;
      }
      return value[0].label;
    }

    return value.label || placeholder;
  };

  return (
    <div
      className={`custom-picky ${className} ${disabled ? "picky_disabled" : ""} ${isOpen ? "open" : ""}`}
      ref={dropdownRef}
    >
      <div className="picky__input" onClick={handleToggle}>
        <span className="picky__value">{getDisplayValue()}</span>
        <DropdownButton
          size="sm"
          title=""
          variant=""
          disabled={disabled}
          className="picky__dropdown-button"
        />
      </div>

      {isOpen && (
        <div className="picky__dropdown">
          <div className="picky__filter">
            <input
              ref={inputRef}
              type="text"
              placeholder="Filter..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="picky__filter-input"
            />
          </div>

          <div className="picky__options">
            {multiple && filteredOptions.length > 0 && (
              <div
                className={`picky__option picky__option--select-all ${filteredOptions.every((option) => isSelected(option)) ? "picky__option--selected" : ""}`}
                onClick={handleSelectAll}
              >
                <input
                  type="checkbox"
                  checked={filteredOptions.every((option) =>
                    isSelected(option)
                  )}
                  onChange={() => {}}
                  className="picky__radio"
                />
                <span className="picky__option-label">Select All</span>
              </div>
            )}
            {filteredOptions.map((option, index) => (
              <div
                key={option.value}
                className={`picky__option ${isSelected(option) ? "picky__option--selected" : ""}`}
                onClick={() => handleOptionClick(option)}
              >
                <input
                  type={multiple ? "checkbox" : "radio"}
                  checked={isSelected(option)}
                  onChange={() => {}}
                  className="picky__radio"
                />
                <span className="picky__option-label">{option.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomPicky;
